import { Department } from './knowledgeBase';

// ============================================
// TICKET TYPE
// ============================================

export const TicketType = {
  INCIDENT: 'Incident',
  REQUEST: 'Request',
  PROBLEM: 'Problem',
  CHANGE: 'Change',
} as const;
export type TicketType = (typeof TicketType)[keyof typeof TicketType];

export const TICKET_TYPES = Object.values(TicketType);

/** Ticket types that can only be created by IT staff */
export const IT_ONLY_TICKET_TYPES: TicketType[] = [TicketType.PROBLEM, TicketType.CHANGE];

/** Check if a ticket type requires IT staff to create */
export function isITOnlyTicketType(ticketType: TicketType): boolean {
  return IT_ONLY_TICKET_TYPES.includes(ticketType);
}

// ============================================
// APPROVAL STATUS (shared by Request and Change)
// ============================================

export const ApprovalStatus = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  NOT_REQUIRED: 'Not Required',
  CANCELLED: 'Cancelled',
} as const;
export type ApprovalStatus = (typeof ApprovalStatus)[keyof typeof ApprovalStatus];

export const APPROVAL_STATUSES = Object.values(ApprovalStatus);

// ============================================
// CHANGE TYPE (Standard, Normal, Emergency)
// ============================================

export const ChangeType = {
  STANDARD: 'Standard',
  NORMAL: 'Normal',
  EMERGENCY: 'Emergency',
} as const;
export type ChangeType = (typeof ChangeType)[keyof typeof ChangeType];

export const CHANGE_TYPES = Object.values(ChangeType);

// ============================================
// RISK LEVEL (for Change tickets)
// ============================================

export const RiskLevel = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  CRITICAL: 'Critical',
} as const;
export type RiskLevel = (typeof RiskLevel)[keyof typeof RiskLevel];

export const RISK_LEVELS = Object.values(RiskLevel);

// ============================================
// CHANGE CATEGORY (for Change tickets)
// ============================================

export const ChangeCategory = {
  INFRASTRUCTURE: 'Infrastructure',
  APPLICATION: 'Application',
  NETWORK: 'Network',
  DATABASE: 'Database',
  SECURITY: 'Security',
  OTHER: 'Other',
} as const;
export type ChangeCategory = (typeof ChangeCategory)[keyof typeof ChangeCategory];

export const CHANGE_CATEGORIES = Object.values(ChangeCategory);

// ============================================
// INCIDENT CATEGORIES & SUBCATEGORIES
// ============================================

export const IncidentCategory = {
  HARDWARE: 'Hardware',
  SOFTWARE: 'Software',
  NETWORK: 'Network',
  ACCESS: 'Access',
  EMAIL: 'Email',
  OTHER: 'Other',
} as const;
export type IncidentCategory = (typeof IncidentCategory)[keyof typeof IncidentCategory];

export const INCIDENT_CATEGORIES = Object.values(IncidentCategory);

export const IncidentSubcategory = {
  // Hardware
  LAPTOP: 'Laptop',
  DESKTOP: 'Desktop',
  MONITOR: 'Monitor',
  KEYBOARD_MOUSE: 'Keyboard/Mouse',
  PRINTER: 'Printer',
  PHONE: 'Phone',
  HEADSET: 'Headset',
  DOCKING_STATION: 'Docking Station',
  OTHER_HARDWARE: 'Other Hardware',
  // Software
  OFFICE_365: 'Office 365',
  TEAMS: 'Teams',
  ONEDRIVE: 'OneDrive',
  SHAREPOINT: 'SharePoint',
  BROWSER: 'Browser',
  CUSTOM_APP: 'Custom Application',
  OTHER_SOFTWARE: 'Other Software',
  // Network
  VPN: 'VPN',
  WIFI: 'WiFi',
  ETHERNET: 'Ethernet/LAN',
  NETWORK_DRIVE: 'Network Drive',
  INTERNET: 'Internet Connectivity',
  // Access
  PASSWORD_RESET: 'Password Reset',
  ACCOUNT_LOCKED: 'Account Locked',
  MFA_ISSUE: 'MFA Issue',
  PERMISSION_ISSUE: 'Permission Issue',
  // Email
  OUTLOOK: 'Outlook',
  CALENDAR: 'Calendar',
  DISTRIBUTION_LIST: 'Distribution List',
  MAILBOX_FULL: 'Mailbox Full',
  EMAIL_SYNC: 'Email Sync',
  // Other
  GENERAL: 'General',
} as const;
export type IncidentSubcategory = (typeof IncidentSubcategory)[keyof typeof IncidentSubcategory];

/** Mapping of incident categories to their subcategories */
export const INCIDENT_SUBCATEGORY_MAP: Record<IncidentCategory, IncidentSubcategory[]> = {
  [IncidentCategory.HARDWARE]: [
    IncidentSubcategory.LAPTOP,
    IncidentSubcategory.DESKTOP,
    IncidentSubcategory.MONITOR,
    IncidentSubcategory.KEYBOARD_MOUSE,
    IncidentSubcategory.PRINTER,
    IncidentSubcategory.PHONE,
    IncidentSubcategory.HEADSET,
    IncidentSubcategory.DOCKING_STATION,
    IncidentSubcategory.OTHER_HARDWARE,
  ],
  [IncidentCategory.SOFTWARE]: [
    IncidentSubcategory.OFFICE_365,
    IncidentSubcategory.TEAMS,
    IncidentSubcategory.ONEDRIVE,
    IncidentSubcategory.SHAREPOINT,
    IncidentSubcategory.BROWSER,
    IncidentSubcategory.CUSTOM_APP,
    IncidentSubcategory.OTHER_SOFTWARE,
  ],
  [IncidentCategory.NETWORK]: [
    IncidentSubcategory.VPN,
    IncidentSubcategory.WIFI,
    IncidentSubcategory.ETHERNET,
    IncidentSubcategory.NETWORK_DRIVE,
    IncidentSubcategory.INTERNET,
  ],
  [IncidentCategory.ACCESS]: [
    IncidentSubcategory.PASSWORD_RESET,
    IncidentSubcategory.ACCOUNT_LOCKED,
    IncidentSubcategory.MFA_ISSUE,
    IncidentSubcategory.PERMISSION_ISSUE,
  ],
  [IncidentCategory.EMAIL]: [
    IncidentSubcategory.OUTLOOK,
    IncidentSubcategory.CALENDAR,
    IncidentSubcategory.DISTRIBUTION_LIST,
    IncidentSubcategory.MAILBOX_FULL,
    IncidentSubcategory.EMAIL_SYNC,
  ],
  [IncidentCategory.OTHER]: [IncidentSubcategory.GENERAL],
};

// ============================================
// REQUEST CATEGORIES & SUBCATEGORIES
// ============================================

export const RequestCategory = {
  NEW_HARDWARE: 'New Hardware',
  SOFTWARE_INSTALL: 'Software Install',
  ACCESS_REQUEST: 'Access Request',
  ACCOUNT_SETUP: 'Account Setup',
  EQUIPMENT_RETURN: 'Equipment Return',
  OTHER: 'Other',
} as const;
export type RequestCategory = (typeof RequestCategory)[keyof typeof RequestCategory];

export const REQUEST_CATEGORIES = Object.values(RequestCategory);

export const RequestSubcategory = {
  // New Hardware
  NEW_LAPTOP: 'New Laptop',
  NEW_DESKTOP: 'New Desktop',
  NEW_MONITOR: 'New Monitor',
  NEW_PERIPHERAL: 'New Peripheral',
  NEW_PHONE: 'New Phone',
  HARDWARE_UPGRADE: 'Hardware Upgrade',
  // Software Install
  STANDARD_SOFTWARE: 'Standard Software',
  LICENSED_SOFTWARE: 'Licensed Software',
  DEVELOPER_TOOLS: 'Developer Tools',
  SPECIALTY_SOFTWARE: 'Specialty Software',
  // Access Request
  SHARED_DRIVE: 'Shared Drive Access',
  APPLICATION_ACCESS: 'Application Access',
  VPN_ACCESS: 'VPN Access',
  ADMIN_ACCESS: 'Admin Access',
  DISTRIBUTION_LIST_ADD: 'Distribution List',
  // Account Setup
  NEW_HIRE: 'New Hire Setup',
  CONTRACTOR: 'Contractor Account',
  SERVICE_ACCOUNT: 'Service Account',
  // Equipment Return
  LAPTOP_RETURN: 'Laptop Return',
  FULL_EQUIPMENT_RETURN: 'Full Equipment Return',
  TERMINATION: 'Termination Handoff',
  // Other
  GENERAL_REQUEST: 'General Request',
} as const;
export type RequestSubcategory = (typeof RequestSubcategory)[keyof typeof RequestSubcategory];

/** Mapping of request categories to their subcategories */
export const REQUEST_SUBCATEGORY_MAP: Record<RequestCategory, RequestSubcategory[]> = {
  [RequestCategory.NEW_HARDWARE]: [
    RequestSubcategory.NEW_LAPTOP,
    RequestSubcategory.NEW_DESKTOP,
    RequestSubcategory.NEW_MONITOR,
    RequestSubcategory.NEW_PERIPHERAL,
    RequestSubcategory.NEW_PHONE,
    RequestSubcategory.HARDWARE_UPGRADE,
  ],
  [RequestCategory.SOFTWARE_INSTALL]: [
    RequestSubcategory.STANDARD_SOFTWARE,
    RequestSubcategory.LICENSED_SOFTWARE,
    RequestSubcategory.DEVELOPER_TOOLS,
    RequestSubcategory.SPECIALTY_SOFTWARE,
  ],
  [RequestCategory.ACCESS_REQUEST]: [
    RequestSubcategory.SHARED_DRIVE,
    RequestSubcategory.APPLICATION_ACCESS,
    RequestSubcategory.VPN_ACCESS,
    RequestSubcategory.ADMIN_ACCESS,
    RequestSubcategory.DISTRIBUTION_LIST_ADD,
  ],
  [RequestCategory.ACCOUNT_SETUP]: [
    RequestSubcategory.NEW_HIRE,
    RequestSubcategory.CONTRACTOR,
    RequestSubcategory.SERVICE_ACCOUNT,
  ],
  [RequestCategory.EQUIPMENT_RETURN]: [
    RequestSubcategory.LAPTOP_RETURN,
    RequestSubcategory.FULL_EQUIPMENT_RETURN,
    RequestSubcategory.TERMINATION,
  ],
  [RequestCategory.OTHER]: [RequestSubcategory.GENERAL_REQUEST],
};

// ============================================
// PROBLEM CATEGORIES & SUBCATEGORIES
// ============================================

export const ProblemCategory = {
  RECURRING_INCIDENT: 'Recurring Incident',
  SYSTEM_FAILURE: 'System Failure',
  PERFORMANCE: 'Performance',
  SECURITY: 'Security',
  INFRASTRUCTURE: 'Infrastructure',
  APPLICATION: 'Application',
  OTHER: 'Other',
} as const;
export type ProblemCategory = (typeof ProblemCategory)[keyof typeof ProblemCategory];

export const PROBLEM_CATEGORIES = Object.values(ProblemCategory);

export const ProblemSubcategory = {
  // Recurring Incident
  REPEATED_OUTAGE: 'Repeated Outage',
  INTERMITTENT_FAILURE: 'Intermittent Failure',
  CHRONIC_ISSUE: 'Chronic Issue',
  // System Failure
  HARDWARE_FAILURE: 'Hardware Failure',
  SOFTWARE_CRASH: 'Software Crash',
  SERVICE_OUTAGE: 'Service Outage',
  DATA_CORRUPTION: 'Data Corruption',
  // Performance
  SLOW_RESPONSE: 'Slow Response',
  RESOURCE_EXHAUSTION: 'Resource Exhaustion',
  CAPACITY_ISSUE: 'Capacity Issue',
  // Security
  VULNERABILITY: 'Vulnerability',
  BREACH_INVESTIGATION: 'Breach Investigation',
  COMPLIANCE_GAP: 'Compliance Gap',
  // Infrastructure
  NETWORK_DEGRADATION: 'Network Degradation',
  STORAGE_ISSUE: 'Storage Issue',
  SERVER_ISSUE: 'Server Issue',
  // Application
  BUG: 'Bug',
  INTEGRATION_FAILURE: 'Integration Failure',
  CONFIGURATION_ERROR: 'Configuration Error',
  // Other
  GENERAL_PROBLEM: 'General Problem',
} as const;
export type ProblemSubcategory = (typeof ProblemSubcategory)[keyof typeof ProblemSubcategory];

/** Mapping of problem categories to their subcategories */
export const PROBLEM_SUBCATEGORY_MAP: Record<ProblemCategory, ProblemSubcategory[]> = {
  [ProblemCategory.RECURRING_INCIDENT]: [
    ProblemSubcategory.REPEATED_OUTAGE,
    ProblemSubcategory.INTERMITTENT_FAILURE,
    ProblemSubcategory.CHRONIC_ISSUE,
  ],
  [ProblemCategory.SYSTEM_FAILURE]: [
    ProblemSubcategory.HARDWARE_FAILURE,
    ProblemSubcategory.SOFTWARE_CRASH,
    ProblemSubcategory.SERVICE_OUTAGE,
    ProblemSubcategory.DATA_CORRUPTION,
  ],
  [ProblemCategory.PERFORMANCE]: [
    ProblemSubcategory.SLOW_RESPONSE,
    ProblemSubcategory.RESOURCE_EXHAUSTION,
    ProblemSubcategory.CAPACITY_ISSUE,
  ],
  [ProblemCategory.SECURITY]: [
    ProblemSubcategory.VULNERABILITY,
    ProblemSubcategory.BREACH_INVESTIGATION,
    ProblemSubcategory.COMPLIANCE_GAP,
  ],
  [ProblemCategory.INFRASTRUCTURE]: [
    ProblemSubcategory.NETWORK_DEGRADATION,
    ProblemSubcategory.STORAGE_ISSUE,
    ProblemSubcategory.SERVER_ISSUE,
  ],
  [ProblemCategory.APPLICATION]: [
    ProblemSubcategory.BUG,
    ProblemSubcategory.INTEGRATION_FAILURE,
    ProblemSubcategory.CONFIGURATION_ERROR,
  ],
  [ProblemCategory.OTHER]: [ProblemSubcategory.GENERAL_PROBLEM],
};

/** Union type for all category types */
export type TicketCategoryType = IncidentCategory | RequestCategory | ProblemCategory | ChangeCategory;

/** Union type for all subcategory types */
export type TicketSubcategoryType = IncidentSubcategory | RequestSubcategory | ProblemSubcategory;

/** Get subcategories for a given ticket type and category */
export function getSubcategoriesForCategory(
  ticketType: TicketType,
  category: TicketCategoryType
): TicketSubcategoryType[] {
  switch (ticketType) {
    case TicketType.INCIDENT:
      return INCIDENT_SUBCATEGORY_MAP[category as IncidentCategory] ?? [];
    case TicketType.REQUEST:
      return REQUEST_SUBCATEGORY_MAP[category as RequestCategory] ?? [];
    case TicketType.PROBLEM:
      return PROBLEM_SUBCATEGORY_MAP[category as ProblemCategory] ?? [];
    case TicketType.CHANGE:
      // Change tickets use ChangeCategory directly, no subcategories
      return [];
    default:
      return [];
  }
}

/** Get categories for a given ticket type */
export function getCategoriesForTicketType(ticketType: TicketType): TicketCategoryType[] {
  switch (ticketType) {
    case TicketType.INCIDENT:
      return INCIDENT_CATEGORIES;
    case TicketType.REQUEST:
      return REQUEST_CATEGORIES;
    case TicketType.PROBLEM:
      return PROBLEM_CATEGORIES;
    case TicketType.CHANGE:
      return CHANGE_CATEGORIES;
    default:
      return [];
  }
}

/** Check if a ticket type uses subcategories */
export function ticketTypeHasSubcategories(ticketType: TicketType): boolean {
  return ticketType !== TicketType.CHANGE;
}

// ============================================
// PRIORITY
// ============================================

export const Priority = {
  P1: 'P1',
  P2: 'P2',
  P3: 'P3',
  P4: 'P4',
} as const;
export type Priority = (typeof Priority)[keyof typeof Priority];

export const TicketStatus = {
  NEW: 'New',
  IN_PROGRESS: 'In Progress',
  WAITING: 'Waiting',
  RESOLVED: 'Resolved',
  CLOSED: 'Closed',
} as const;
export type TicketStatus = (typeof TicketStatus)[keyof typeof TicketStatus];

export const TICKET_STATUSES = Object.values(TicketStatus);

// ============================================
// WAITING REASON (when status is Waiting)
// ============================================

export const WaitingReason = {
  USER: 'User',
  VENDOR: 'Vendor',
  APPROVAL: 'Approval',
  PARTS: 'Parts',
  CHANGE_WINDOW: 'Change Window',
  OTHER: 'Other',
} as const;
export type WaitingReason = (typeof WaitingReason)[keyof typeof WaitingReason];

export const WAITING_REASONS = Object.values(WaitingReason);

/** Get display label for waiting status with reason */
export function getWaitingStatusLabel(reason?: WaitingReason): string {
  if (!reason) return 'Waiting';
  return `Waiting - ${reason}`;
}

// ============================================
// ESCALATION TIER
// ============================================

export const EscalationTier = {
  T1: 'T1',
  T2: 'T2',
  T3: 'T3',
} as const;
export type EscalationTier = (typeof EscalationTier)[keyof typeof EscalationTier];

export const ESCALATION_TIERS = Object.values(EscalationTier);

export const Category = {
  ACCOUNT: 'Account',
  HARDWARE: 'Hardware',
  SOFTWARE: 'Software',
  NETWORK: 'Network',
  UK_CONTRACTS: 'UK Contracts',
  ACCESS: 'Access',
  EMAIL: 'Email',
  OTHER: 'Other',
} as const;
export type Category = (typeof Category)[keyof typeof Category];

export const Location = {
  US_DEARBORN: 'US - Dearborn',
  US_HOUSTON: 'US - Houston',
  US_MELBOURNE: 'US - Melbourne',
  UK_DAVENTRY: 'UK - Daventry',
  UK_DUNTON: 'UK - Dunton',
  UK_GLASGOW: 'UK - Glasgow',
  PH_CAINTA: 'PH - Cainta',
  AF_SOUTH_AFRICA: 'AF - South Africa',
  EU_GERMANY: 'EU - Germany',
  EU_GREECE: 'EU - Greece',
  EU_BULGARIA: 'EU - Bulgaria',
  OC_AUSTRALIA: 'OC - Australia',
  OC_THAILAND: 'OC - Thailand',
  NA_MEXICO: 'NA - Mexico',
  SA_BRAZIL: 'SA - Brazil',
} as const;
export type Location = (typeof Location)[keyof typeof Location];

export const Region = {
  US: 'US',
  UK: 'UK',
  EU: 'EU',
  PH: 'PH',
  AF: 'AF',
  OC: 'OC',
  NA: 'NA',
  SA: 'SA',
} as const;
export type Region = (typeof Region)[keyof typeof Region];

export const TechTier = {
  T1: 'T1',
  T2: 'T2',
} as const;
export type TechTier = (typeof TechTier)[keyof typeof TechTier];

export const UserRole = {
  ADMIN: 'Admin',
  T1_TECH: 'T1 Tech',
  T2_TECH: 'T2 Tech',
} as const;
export type UserRole = (typeof UserRole)[keyof typeof UserRole];

export const UserStatus = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
} as const;
export type UserStatus = (typeof UserStatus)[keyof typeof UserStatus];

export const TechWorkStatus = {
  WORKING: 'Working',
  AVAILABLE: 'Available',
  BREAK: 'Break',
  LUNCH: 'Lunch',
  OFFLINE: 'Offline',
} as const;
export type TechWorkStatus = (typeof TechWorkStatus)[keyof typeof TechWorkStatus];

// ============================================
// TICKET CHANNEL (how the ticket was received)
// ============================================

export const TicketChannel = {
  PHONE: 'Phone',
  CHAT: 'Chat',
  CHATBOT: 'Chatbot',
  EMAIL: 'Email',
  TEAMS: 'Teams',
  SELF_SERVICE: 'Self-Service',
} as const;
export type TicketChannel = (typeof TicketChannel)[keyof typeof TicketChannel];

export const TICKET_CHANNELS = Object.values(TicketChannel);

// ============================================
// ASSIGNMENT GROUP
// ============================================

export const AssignmentGroup = {
  SERVICE_DESK: 'Service Desk',
  SYSTEMS: 'Systems',
  NETWORKING: 'Networking',
  SECURITY: 'Security',
  APPLICATIONS: 'Applications',
  INFRASTRUCTURE: 'Infrastructure',
  DATABASE: 'Database',
  TELECOM: 'Telecom',
} as const;
export type AssignmentGroup = (typeof AssignmentGroup)[keyof typeof AssignmentGroup];

export const ASSIGNMENT_GROUPS = Object.values(AssignmentGroup);

// ============================================
// CLIENT LOB (Line of Business / Client)
// ============================================

export const ClientLOB = {
  FORD: 'Ford',
  LINCOLN: 'Lincoln',
  FORD_PRO: 'Ford Pro',
  FORD_CREDIT: 'Ford Credit',
  PERCEPTA_INTERNAL: 'Percepta Internal',
} as const;
export type ClientLOB = (typeof ClientLOB)[keyof typeof ClientLOB];

export const CLIENT_LOBS = Object.values(ClientLOB);

// ============================================
// LOB PROGRAM (Program/Job within a Client LOB)
// ============================================

export const LOBProgram = {
  // Ford Programs
  TRUCK_CONCERN: 'Truck Concern',
  CRC: 'CRC',
  INBOUND_CONCERN: 'Inbound Concern',
  OUTBOUND: 'Outbound',
  DIGITAL: 'Digital',
  SOCIAL_MEDIA: 'Social Media',
  EXECUTIVE_OFFICE: 'Executive Office',
  // Lincoln Programs
  LINCOLN_CRC: 'Lincoln CRC',
  LINCOLN_CONCIERGE: 'Lincoln Concierge',
  // Ford Pro Programs
  FORD_PRO_FINSERV: 'Ford Pro FinServ',
  FORD_PRO_COMMERCIAL: 'Ford Pro Commercial',
  // Ford Credit Programs
  FORD_CREDIT_SERVICING: 'Ford Credit Servicing',
  FORD_CREDIT_COLLECTIONS: 'Ford Credit Collections',
  // Percepta Internal Programs
  GA: 'G&A',
  IT: 'IT',
  HR: 'HR',
  TRAINING: 'Training',
  QUALITY: 'Quality',
  WFM: 'WFM',
} as const;
export type LOBProgram = (typeof LOBProgram)[keyof typeof LOBProgram];

export const LOB_PROGRAMS = Object.values(LOBProgram);

/** Mapping of Client LOB to available programs */
export const LOB_PROGRAM_MAP: Record<ClientLOB, LOBProgram[]> = {
  [ClientLOB.FORD]: [
    LOBProgram.TRUCK_CONCERN,
    LOBProgram.CRC,
    LOBProgram.INBOUND_CONCERN,
    LOBProgram.OUTBOUND,
    LOBProgram.DIGITAL,
    LOBProgram.SOCIAL_MEDIA,
    LOBProgram.EXECUTIVE_OFFICE,
  ],
  [ClientLOB.LINCOLN]: [
    LOBProgram.LINCOLN_CRC,
    LOBProgram.LINCOLN_CONCIERGE,
  ],
  [ClientLOB.FORD_PRO]: [
    LOBProgram.FORD_PRO_FINSERV,
    LOBProgram.FORD_PRO_COMMERCIAL,
  ],
  [ClientLOB.FORD_CREDIT]: [
    LOBProgram.FORD_CREDIT_SERVICING,
    LOBProgram.FORD_CREDIT_COLLECTIONS,
  ],
  [ClientLOB.PERCEPTA_INTERNAL]: [
    LOBProgram.GA,
    LOBProgram.IT,
    LOBProgram.HR,
    LOBProgram.TRAINING,
    LOBProgram.QUALITY,
    LOBProgram.WFM,
  ],
};

/** Get programs for a given LOB */
export function getProgramsForLOB(lob: ClientLOB): LOBProgram[] {
  return LOB_PROGRAM_MAP[lob] ?? [];
}

// ============================================
// LOB SITE (Physical location)
// ============================================

export const LOBSite = {
  // US Sites
  US_DEARBORN: 'US - Dearborn',
  US_HOUSTON: 'US - Houston',
  US_MELBOURNE: 'US - Melbourne',
  // UK Sites
  UK_GLASGOW: 'UK - Glasgow',
  UK_DAVENTRY: 'UK - Daventry',
  UK_DUNTON: 'UK - Dunton',
  // Philippines Sites
  PH_CAINTA: 'PH - Cainta',
  // Other International
  AF_SOUTH_AFRICA: 'AF - South Africa',
  EU_GERMANY: 'EU - Germany',
  OC_AUSTRALIA: 'OC - Australia',
  NA_MEXICO: 'NA - Mexico',
} as const;
export type LOBSite = (typeof LOBSite)[keyof typeof LOBSite];

export const LOB_SITES = Object.values(LOBSite);

/** Mapping of Client LOB to available sites */
export const LOB_SITE_MAP: Record<ClientLOB, LOBSite[]> = {
  [ClientLOB.FORD]: [
    LOBSite.US_DEARBORN,
    LOBSite.US_HOUSTON,
    LOBSite.US_MELBOURNE,
    LOBSite.UK_GLASGOW,
    LOBSite.UK_DAVENTRY,
    LOBSite.PH_CAINTA,
  ],
  [ClientLOB.LINCOLN]: [
    LOBSite.US_DEARBORN,
    LOBSite.US_HOUSTON,
  ],
  [ClientLOB.FORD_PRO]: [
    LOBSite.US_DEARBORN,
    LOBSite.US_HOUSTON,
  ],
  [ClientLOB.FORD_CREDIT]: [
    LOBSite.US_DEARBORN,
    LOBSite.US_HOUSTON,
    LOBSite.PH_CAINTA,
  ],
  [ClientLOB.PERCEPTA_INTERNAL]: [
    LOBSite.US_DEARBORN,
    LOBSite.US_HOUSTON,
    LOBSite.UK_GLASGOW,
    LOBSite.UK_DAVENTRY,
    LOBSite.PH_CAINTA,
  ],
};

/** Get sites for a given LOB */
export function getSitesForLOB(lob: ClientLOB): LOBSite[] {
  return LOB_SITE_MAP[lob] ?? [];
}

// ============================================
// URGENCY (different from Priority for ITIL alignment)
// ============================================

export const Urgency = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
} as const;
export type Urgency = (typeof Urgency)[keyof typeof Urgency];

export const URGENCIES = Object.values(Urgency);

export const JobTag = {
  HARDWARE: 'Hardware',
  LAPTOP: 'Laptop',
  DESKTOP: 'Desktop',
  MONITOR: 'Monitor',
  KEYBOARD_MOUSE: 'Keyboard/Mouse',
  PRINTER: 'Printer',
  PHONE: 'Phone',
  HEADSET: 'Headset',
  DOCKING_STATION: 'Docking Station',
  SOFTWARE: 'Software',
  O365: 'O365',
  EMAIL: 'Email',
  TEAMS: 'Teams',
  ONEDRIVE: 'OneDrive',
  SHAREPOINT: 'SharePoint',
  SOFTWARE_INSTALL: 'Software Install',
  LICENSE: 'License',
  IDM_ONBOARDING: 'IDM/Onboarding',
  ACCOUNT_SETUP: 'Account Setup',
  PASSWORD_RESET: 'Password Reset',
  MFA: 'MFA',
  ACCESS_REQUEST: 'Access Request',
  TERMINATION: 'Termination',
  UK_SPECIFIC: 'UK-specific',
  NETWORKING: 'Networking',
  VPN: 'VPN',
  WIFI: 'WiFi',
  ETHERNET: 'Ethernet',
  NETWORK_DRIVE: 'Network Drive',
  FIREWALL: 'Firewall',
} as const;
export type JobTag = (typeof JobTag)[keyof typeof JobTag];

export interface TagCategory {
  id: string;
  label: string;
  tags: JobTag[];
}

export const TAG_CATEGORIES: TagCategory[] = [
  {
    id: 'hardware',
    label: 'Hardware',
    tags: [
      JobTag.HARDWARE,
      JobTag.LAPTOP,
      JobTag.DESKTOP,
      JobTag.MONITOR,
      JobTag.KEYBOARD_MOUSE,
      JobTag.PRINTER,
      JobTag.PHONE,
      JobTag.HEADSET,
      JobTag.DOCKING_STATION,
    ],
  },
  {
    id: 'software',
    label: 'Software',
    tags: [
      JobTag.SOFTWARE,
      JobTag.O365,
      JobTag.EMAIL,
      JobTag.TEAMS,
      JobTag.ONEDRIVE,
      JobTag.SHAREPOINT,
      JobTag.SOFTWARE_INSTALL,
      JobTag.LICENSE,
    ],
  },
  {
    id: 'idm',
    label: 'IDM/Onboarding',
    tags: [
      JobTag.IDM_ONBOARDING,
      JobTag.ACCOUNT_SETUP,
      JobTag.PASSWORD_RESET,
      JobTag.MFA,
      JobTag.ACCESS_REQUEST,
      JobTag.TERMINATION,
      JobTag.UK_SPECIFIC,
    ],
  },
  {
    id: 'networking',
    label: 'Networking',
    tags: [
      JobTag.NETWORKING,
      JobTag.VPN,
      JobTag.WIFI,
      JobTag.ETHERNET,
      JobTag.NETWORK_DRIVE,
      JobTag.FIREWALL,
    ],
  },
];

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  department: Department;
  status: UserStatus;
  location?: Location;
  /** Employee ID/Number */
  employeeId?: string;
  /** Corporate Directory System ID */
  cdsid?: string;
  /** Client Line of Business */
  clientLOB?: ClientLOB;
  /** Program within the LOB */
  lobProgram?: LOBProgram;
  /** Physical site location */
  lobSite?: LOBSite;
  createdAt: Date;
  updatedAt: Date;
  lastLogin?: Date;
}

export interface Tech {
  id: string;
  name: string;
  tier: TechTier;
  region: Region;
  location: Location;
  skills: JobTag[];
  currentLoad: number;
  maxLoad: number;
  isAvailable: boolean;
  userId?: string;
  workStatus?: TechWorkStatus;
  workStartTime?: Date;
  currentTicketId?: string;
  /** Assignment group this tech belongs to (e.g., Service Desk, Networking) */
  assignmentGroup?: AssignmentGroup;
}

// ============================================
// REQUESTER INFO (for user search/lookup)
// ============================================

export interface RequesterInfo {
  id: string;
  name: string;
  email: string;
  department: Department;
  location?: Location;
  /** Employee ID/Number */
  employeeId?: string;
  /** Corporate Directory System ID */
  cdsid?: string;
  /** Client Line of Business */
  clientLOB?: ClientLOB;
  /** Program within the LOB */
  lobProgram?: LOBProgram;
  /** Physical site location */
  lobSite?: LOBSite;
}

// ============================================
// TICKET - BASE INTERFACE
// ============================================

export interface BaseTicket {
  id: string;
  ticketNumber: string;
  shortDescription: string;
  ticketType: TicketType;
  priority: Priority;
  status: TicketStatus;
  location: Location;
  assignedTechId: string | null;

  /** User ID who the ticket is for (the affected user) */
  requesterId: string;

  /** Denormalized requester name for display */
  requesterName: string;

  /** Denormalized requester email for display */
  requesterEmail: string;

  /** Requester's department from AD */
  requesterDepartment: Department;

  /** User ID who created the ticket (may differ from requesterId) */
  createdBy: string;

  /** Denormalized creator name for display */
  createdByName: string;

  createdAt: Date;
  updatedAt: Date;
  slaDeadline: Date | null;
  notes?: string;

  /** Waiting reason when status is 'Waiting' */
  waitingReason?: WaitingReason;

  /** Custom note when waitingReason is 'Other' */
  waitingReasonNote?: string;

  /** Assignment group (team) */
  assignmentGroup?: AssignmentGroup;

  /** Escalation tier */
  escalationTier?: EscalationTier;

  /** Resolution notes (required when resolving) */
  resolutionNotes?: string;

  /** When ticket was first responded to */
  firstResponseAt?: Date;

  /** When ticket was resolved */
  resolvedAt?: Date;

  /** When ticket was closed */
  closedAt?: Date;

  /** Name of assigned tech (denormalized for display) */
  assignedTechName?: string;

  /**
   * @deprecated Use category and subcategory instead.
   * Kept for backward compatibility during migration.
   */
  tags?: JobTag[];
}

// ============================================
// TYPE-SPECIFIC TICKET INTERFACES
// ============================================

/** Incident ticket - something broken or not working */
export interface IncidentTicket extends BaseTicket {
  ticketType: typeof TicketType.INCIDENT;
  category: IncidentCategory;
  subcategory: IncidentSubcategory;
}

/** Request ticket - request for something new (approval-based) */
export interface RequestTicket extends BaseTicket {
  ticketType: typeof TicketType.REQUEST;
  category: RequestCategory;
  subcategory: RequestSubcategory;

  /** Approval status for the request */
  approvalStatus: ApprovalStatus;

  /** Date by which the request is needed */
  neededByDate?: Date;

  /** Name of the item being requested */
  requestedItemName?: string;
}

/** Problem ticket - root cause analysis (IT only) */
export interface ProblemTicket extends BaseTicket {
  ticketType: typeof TicketType.PROBLEM;
  category: ProblemCategory;
  subcategory: ProblemSubcategory;

  /** Detailed problem statement (min 50 chars) */
  problemStatement: string;

  /** Identified root cause (can be updated after creation) */
  rootCause?: string;

  /** Temporary workaround while root cause is being addressed */
  workaround?: string;

  /** IDs of incident tickets linked to this problem */
  linkedIncidentIds: string[];
}

/** Change ticket - planned modification (IT only) */
export interface ChangeTicket extends BaseTicket {
  ticketType: typeof TicketType.CHANGE;

  /** Change uses ChangeCategory directly instead of category/subcategory */
  changeCategory: ChangeCategory;

  /** Type of change: Standard, Normal, Emergency */
  changeType: ChangeType;

  /** Risk level assessment */
  riskLevel: RiskLevel;

  /** Approval status for the change */
  approvalStatus: ApprovalStatus;

  /** Rollback plan if change needs to be reverted (min 20 chars) */
  rollbackPlan: string;

  /** Planned start of implementation window */
  implementationStart?: Date;

  /** Planned end of implementation window */
  implementationEnd?: Date;
}

/** Discriminated union of all ticket types */
export type Ticket = IncidentTicket | RequestTicket | ProblemTicket | ChangeTicket;

// ============================================
// TYPE GUARDS
// ============================================

/** Type guard for Incident tickets */
export function isIncidentTicket(ticket: Ticket): ticket is IncidentTicket {
  return ticket.ticketType === TicketType.INCIDENT;
}

/** Type guard for Request tickets */
export function isRequestTicket(ticket: Ticket): ticket is RequestTicket {
  return ticket.ticketType === TicketType.REQUEST;
}

/** Type guard for Problem tickets */
export function isProblemTicket(ticket: Ticket): ticket is ProblemTicket {
  return ticket.ticketType === TicketType.PROBLEM;
}

/** Type guard for Change tickets */
export function isChangeTicket(ticket: Ticket): ticket is ChangeTicket {
  return ticket.ticketType === TicketType.CHANGE;
}

/** Get category from ticket (handles Change tickets which use changeCategory) */
export function getTicketCategory(ticket: Ticket): TicketCategoryType {
  if (isChangeTicket(ticket)) {
    return ticket.changeCategory;
  }
  return ticket.category;
}

/** Get subcategory from ticket (returns undefined for Change tickets) */
export function getTicketSubcategory(ticket: Ticket): TicketSubcategoryType | undefined {
  if (isChangeTicket(ticket)) {
    return undefined;
  }
  return ticket.subcategory;
}

export interface CreateJobFormData {
  ticketNumber: string;
  shortDescription: string;
  priority: Priority;
  location: Location;
  tags: JobTag[];
  notes?: string;
}

export interface TicketFilters {
  priorities: Priority[];
  statuses: TicketStatus[];
  locations: Location[];
  ticketTypes: TicketType[];
  categories: TicketCategoryType[];
  searchQuery: string;
  /** @deprecated Use categories instead */
  tags?: JobTag[];
}

export interface AssignmentResult {
  techId: string;
  techName: string;
  location: Location;
  matchScore: number;
  reasons: string[];
}

export interface CreateUserFormData {
  email: string;
  name: string;
  role: UserRole;
  department: Department;
  status: UserStatus;
}

export interface UpdateUserFormData extends Partial<CreateUserFormData> {
  id: string;
}

export interface UserFilters {
  roles: UserRole[];
  statuses: UserStatus[];
  searchQuery: string;
}
